import React, { useEffect } from 'react';

import './success.css';
import { Trans, useTranslation } from 'react-i18next';

import SuccessTable, { SuccessTableItem } from './success-table';
import { PaymentLabel } from '../../@types/types';
import { SuccessIcon } from '../../assets/svg/successIcon';
import { useFeature } from '../../featureFlags';
import { useAgent } from '../../newrelic';
import { WidgetActions, WidgetId } from '../../shared/constants';
import { formatNumberForDisplay } from '../../shared/utils';
import { Button } from '../button';
import { PoweredByPushpay } from '../pushpay/poweredByPushpay';
import TermsAndPolicy from '../terms/terms';

export type SuccessProps = {
	minHeight?: number;
	dismiss: () => void;
	paymentLabel: PaymentLabel;
	merchant: string;
	currency: string;
	emailAddress: string;
	policyUrl: string;
	termsUrl: string;
	symbol: string;
	checkEmail?: boolean;
	parentRef: React.RefObject<HTMLDivElement | null>;
	tableItems: SuccessTableItem[];
};

const MIN_HEIGHT = 516;
const Success: React.FC<SuccessProps> = ({
	paymentLabel,
	dismiss,
	minHeight = MIN_HEIGHT,
	checkEmail,
	emailAddress,
	policyUrl,
	termsUrl,
	merchant,
	currency,
	symbol,
	tableItems,
	parentRef,
}) => {
	const agent = useAgent();
	const { t: translate } = useTranslation();

	const multiFundsEnabled = useFeature('MultiFunds');
	const showTable = multiFundsEnabled && tableItems && tableItems.length > 1;

	useEffect(() => {
		agent?.recordCustomEvent(WidgetId, {
			type: WidgetActions.successPage,
		});
	}, [agent]);

	useEffect(() => {
		parentRef.current?.scrollIntoView({ behavior: 'auto', block: 'center' });
	}, [parentRef]);

	if (tableItems.length === 0) {
		return <></>;
	}

	const amount = tableItems.reduce((sum, item) => sum + parseFloat(item.amount), 0).toFixed(2);
	return (
		<div className="success" style={showTable ? { minHeight: 'auto', maxHeight: '100%' } : { minHeight }}>
			<div className="success-content">
				<SuccessIcon height="98" width="103" />
				<h3 className="success-title">{translate('success.thankYou')}</h3>
				{showTable && (
					<SuccessTable items={tableItems} symbol={symbol} total={formatNumberForDisplay(amount)} />
				)}
				<div className="success-description">
					{showTable ? (
						<Trans
							components={{ bold: <strong /> }}
							i18nKey="success.minimalDescription"
							values={{
								label: paymentLabel.NounLowerCase,
								merchant,
							}}
						/>
					) : (
						<Trans
							components={{ bold: <strong /> }}
							i18nKey="success.description"
							values={{
								label: paymentLabel.NounLowerCase,
								currency,
								symbol,
								amount: formatNumberForDisplay(tableItems[0].amount),
								fund: tableItems[0].fundName,
								merchant,
							}}
						/>
					)}
				</div>
				{checkEmail && emailAddress && (
					<p className="success-confirm">{translate('success.confirmation', { emailAddress })}</p>
				)}
				<Button
					label={translate('success.again', { label: paymentLabel.VerbSentenceCase })}
					type="button"
					onClick={dismiss}
				/>
				<PoweredByPushpay />
				<TermsAndPolicy
					label={paymentLabel.NounLowerCase}
					name={merchant}
					policyUrl={policyUrl}
					termsUrl={termsUrl}
					minimal
				/>
			</div>
		</div>
	);
};

export default Success;
